import { HEADER_HEIGHT, SECOND_HEADER_HEIGHT } from "@/constants/components";

import {
  colors,
  HStack,
  TabBar,
  GoiLogo,
  Spacing,
  GLOBAL_MAX_WIDTH,
  customEvent,
  EventTriggerAndTargetType,
  Hamburger,
  useResponsive,
  VStack,
  responsive,
  call,
  getPrimeUrl,
  OverlayModal,
  BaseButton,
  ColorIcon,
} from "goi_common";
import {
  baseColors,
  baseRadius,
  BaseStack,
  baseSpacing,
} from "goi_common/src/design-system";
import { useRouter } from "next/router";
import { useFunnelData } from "@/utils/getFunnelRouteDetail";

import { useMemo } from "react";
import type { ReactNode } from "react";

import { Box } from "@chakra-ui/react";

import Link from "next/link";

import reviewData from "goi_common/src/constants/reviewCount.json";
import HeaderButton from "./HeaderButton";
import ConsultCallButton from "@/components/consult/ConsultCallButton";
import { CONSULT_HOURS_LABEL } from "@/constants/consult";
import { sessionStorage, storageKeys } from "@/utils/storage";
import { parseToPrimeUrl } from "@/utils/parseToPrimeUrl";
import { useOverlay } from "@toss/use-overlay";

const MENUS: {
  text: string;
  to: string;
  href?: string;
  eventDetails: EventTriggerAndTargetType["gnb"][];
  withFromPrime?: boolean;
}[] = [
  {
    text: "읽을거리",
    to: parseToPrimeUrl(getPrimeUrl("/content")),
    href: parseToPrimeUrl(getPrimeUrl("/content")),
    eventDetails: ["nav-uv_guidebook"],
    withFromPrime: true,
  },
  {
    text: "고객후기",
    to: "/review/",
    eventDetails: ["review-uv_review"],
    withFromPrime: true,
  },
  {
    text: "100원상조",
    to: "/",
    href: parseToPrimeUrl(getPrimeUrl("/")),
    eventDetails: ["nav-uv_prime"],
    withFromPrime: false,
  },
  {
    text: "장례견적",
    to: "/match/request/",
    eventDetails: ["nav-uv_match_request"],
    withFromPrime: true,
  },
];

export function Header({ onOpen }: { onOpen: () => void }) {
  const router = useRouter();
  const handleHamburgerClick = () => {
    customEvent({
      newGtm: {
        path: "gnb",
        trigger_and_target: "hamburger",
      },
    });
    onOpen();
  };

  const { isBiggerThanLg } = useResponsive();

  const isRegistrationRoute = router.route.includes("registration");

  /** 계약서 임박 온보딩. 결제 완료 후 온보딩과 헤더 구성을 맞춘다. */
  const isContractOnboarding = router.route === "/contract-check/onboarding";

  const isEvent25G =
    router.route.includes("25-g") ||
    sessionStorage.get(storageKeys.productGroup) === "event_25g";

  const isEvent25H =
    router.route.includes("25-h") ||
    sessionStorage.get(storageKeys.productGroup) === "event_25h";

  const isCWS =
    router.route.includes("cws") ||
    sessionStorage.get(storageKeys.productGroup) === "event_cws";

  const isAdv =
    router.route.includes("adv") ||
    sessionStorage.get(storageKeys.productGroup) === "adv";

  const isBitbunny =
    router.route.includes("bitbunny") ||
    sessionStorage.get(storageKeys.CouponIssuer) === "비트버니";

  const { funnelProduct, funnelRoutePaths } = useFunnelData();

  const isPromotion = funnelProduct && funnelProduct.type === "PROMOTION";

  const isCallLeadLeadform = router.route === "/call-lead/leadform";

  /** 고객센터·햄버거는 두고 탭 바(읽을거리·고객후기 등)만 숨기는 화면 */
  const isTabBarHiddenRoute =
    router.route === "/prime-benefit/not-subscribed" ||
    router.route === "/prime-benefit/verify";

  const isHeaderContentHiddenRoute =
    isRegistrationRoute ||
    isContractOnboarding ||
    router.route.includes("prime-request") ||
    router.route.includes("calculator") ||
    router.route.includes("comparison") ||
    router.route.includes("signed-sangjo") ||
    router.route === "/introduce/quiz" ||
    isEvent25G ||
    isEvent25H ||
    isCWS ||
    isAdv ||
    isPromotion ||
    isCallLeadLeadform;

  const logoHref = useMemo(() => {
    if (!isHeaderContentHiddenRoute) return getPrimeUrl("/");

    if (isCallLeadLeadform) return "/call-lead/leadform";
    if (isEvent25G) return "/promotion/25-g/";
    if (isEvent25H) return "/promotion/25-h/";

    if (isCWS) return "/promotion/cws/";
    if (isAdv) return "/adv/";
    if (isPromotion) return funnelRoutePaths.home || router.asPath;

    if (isBitbunny) return "/registration/coupon/bitbunny/";
    return "/";
  }, [
    isHeaderContentHiddenRoute,
    isCallLeadLeadform,
    isEvent25G,
    isEvent25H,
    isCWS,
    isAdv,
    isPromotion,
  ]);

  const isHeaderLogoHiddenRoute =
    router.route.includes("/payment/failure") ||
    router.route.includes("/payment/easypay-card");

  if (isHeaderLogoHiddenRoute) {
    return null;
  }

  return (
    <header>
      <div
        css={responsive({
          position: "fixed",
          width: "100%",
          height: `${HEADER_HEIGHT}px`,
          top: 0,
          zIndex: 1000,
          backgroundColor: colors.white,
          transition: "none",
          userSelect: "none",
          // 평소엔 탭 바가 구분선을 갖는다. 탭 바를 숨긴 화면은 헤더가 직접 긋는다
          borderBottom: isTabBarHiddenRoute ? "1px solid #E8E9EA" : "none",
        })}
      >
        <VStack
          margin="0 auto"
          css={responsive({
            width: {
              base: "100%",
              lg: `${GLOBAL_MAX_WIDTH}px`,
            },
          })}
        >
          <HStack height={HEADER_HEIGHT}>
            <HStack
              padding="12px 20px"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Link
                id="goi-logo"
                href={logoHref}
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "gnb",
                      trigger_and_target: "logo-uv_home",
                    },
                  });
                }}
              >
                <GoiLogo color={colors.goiBlack} size={32} />
              </Link>
              {(isRegistrationRoute ||
                router.route === "/introduce/quiz" ||
                isContractOnboarding) && (
                <HeaderCallButton label="상담원 연결하기" />
              )}

              {!isHeaderContentHiddenRoute && (
                <HStack
                  alignItems="center"
                  style={{
                    background: "white",
                  }}
                >
                  {!isTabBarHiddenRoute && (
                    <div
                      css={responsive({
                        display: {
                          lg: "block",
                          base: "none",
                        },
                      })}
                    >
                      <HeaderTabBar />
                    </div>
                  )}

                  <Spacing orientation="horizontal" gap={40} />
                  <HeaderCallButton label="24시간 고객센터" />

                  <Spacing orientation="horizontal" gap={16} />
                  <Box position="relative">
                    <button
                      onClick={handleHamburgerClick}
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Hamburger color={colors.gray900} size={24} />
                    </button>
                  </Box>
                </HStack>
              )}
            </HStack>
          </HStack>

          {!isHeaderContentHiddenRoute && !isTabBarHiddenRoute && (
            <div
              css={responsive({
                background: "white",
                display: {
                  lg: "none",
                  base: "block",
                },
                borderBottom: {
                  lg: "none",
                  base: `1px solid ${colors.gray100}`,
                },
              })}
            >
              <HeaderTabBar />
            </div>
          )}
        </VStack>
      </div>
      <Spacing
        gap={
          isHeaderContentHiddenRoute || isTabBarHiddenRoute || isBiggerThanLg
            ? HEADER_HEIGHT // 56
            : HEADER_HEIGHT + SECOND_HEADER_HEIGHT // 100
        }
      />
    </header>
  );
}

function HeaderTabBar() {
  const router = useRouter();
  const { isBiggerThanLg } = useResponsive();
  const reviewCount = reviewData.count;

  return (
    <Box
      padding="0 20px"
      css={{
        overflow: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <TabBar gap={isBiggerThanLg ? 40 : 8}>
        {MENUS.map((item, i) => {
          const itemPathname = item.to.split("?")[0];
          const isActive =
            itemPathname === "/"
              ? router.pathname === "/"
              : router.pathname.startsWith(itemPathname.replace(/\/$/, ""));
          const isReview = item.to === "/review/";

          return (
            <Box
              position="relative"
              key={i}
              css={responsive({
                borderBottom: {
                  base: isActive
                    ? `2px solid ${baseColors.label_normal}`
                    : "none",
                  lg: "none",
                },
              })}
            >
              <Link
                href={
                  item.href ??
                  parseToPrimeUrl(item.to) +
                    (item.withFromPrime ? "?from=prime" : "")
                }
              >
                <TabBar.Tab
                  className={
                    isActive
                      ? isBiggerThanLg
                        ? "headline_2_sb"
                        : "body_1_normal_sb"
                      : isBiggerThanLg
                      ? "headline_2_m"
                      : "body_1_normal_m"
                  }
                  css={responsive({
                    padding: "10px 4px",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    color: {
                      base: isActive
                        ? baseColors.label_normal
                        : baseColors.label_muted_2,
                    },
                  })}
                  onClick={() => {
                    item.eventDetails.forEach((trigger_and_target) => {
                      customEvent({
                        newGtm: {
                          path: "gnb",
                          trigger_and_target,
                        },
                      });
                    });
                    if (item.to === "/") {
                      customEvent({
                        newGtm: {
                          path: "product_main",
                          trigger_and_target: "tab_type-view_type",
                          text: "고이프라임",
                        },
                      });
                    }
                  }}
                >
                  <HStack gap="2px" alignItems="center" justifyContent="center">
                    {item.text}
                    {isReview && reviewCount > 0 && (
                      <Box
                        className="white"
                        padding="1px 6px"
                        backgroundColor={colors.orange700}
                        borderRadius="20px"
                        css={{
                          fontSize: "12px",
                          fontStyle: "normal",
                          fontWeight: "500",
                          lineHeight: "18px",
                        }}
                      >
                        {reviewCount}
                      </Box>
                    )}
                  </HStack>
                </TabBar.Tab>
              </Link>
            </Box>
          );
        })}
      </TabBar>
    </Box>
  );
}

/**
 * 고객센터 모달의 종류.
 * - member: 프라임 홈, 계약서 확인(온보딩 제외). 가입 고객 선택지가 더해진다.
 * - faq: FAQ 전체. 상조 상담 신청 대신 시간 제한이 있는 상담 전화가 들어간다.
 * - default: 그 밖의 모든 경로.
 */
export type CallModalVariant = "default" | "member" | "faq";

const getCallModalVariant = (pathname: string): CallModalVariant => {
  if (pathname.startsWith("/faq")) return "faq";

  const isMemberRoute =
    pathname === "/" ||
    (pathname.startsWith("/contract-check") &&
      pathname !== "/contract-check/onboarding");

  return isMemberRoute ? "member" : "default";
};

function HeaderCallButton({ label }: { label: string }) {
  const overlay = useOverlay();
  const router = useRouter();
  const onOpenCallModal = () => {
    const variant = getCallModalVariant(router.pathname);

    overlay.open(({ isOpen, close: onClose }) => {
      return <CallModal isOpen={isOpen} onClose={onClose} variant={variant} />;
    });
  };

  return (
    <HeaderButton
      path="gnb"
      onClick={onOpenCallModal}
      eventTrigger="srvc_rcmms-uv_funeral_prepare_1"
    >
      {label}
    </HeaderButton>
  );
}

export function CallModal({
  isOpen,
  onClose,
  variant = "default",
  source,
}: {
  isOpen: boolean;
  onClose: () => void;
  variant?: CallModalVariant;
  /** 모달을 연 곳. 있으면 버튼 클릭 이벤트에 properties.source 로 붙는다 */
  source?: string;
}) {
  const router = useRouter();
  const eventProperties = source ? { source } : undefined;

  return (
    <OverlayModal
      onClose={onClose}
      isOpen={isOpen}
      modalContentStyle={{
        minWidth: "320px",
        // 모바일에서 화면을 꽉 채우지 않도록 좌우 여백을 두고, 모서리는 안쪽(ModalBody)과 맞춘다
        width: "calc(100% - 40px)",
        borderRadius: `${baseRadius.rad_12}px`,
      }}
      overlayModalContent={
        <BaseStack
          padding={`${baseSpacing.sp_16}px ${baseSpacing.sp_24}px ${baseSpacing.sp_32}px`}
          spacing={baseSpacing.sp_24}
          width="100%"
        >
          <Box display="flex" justifyContent="flex-end">
            <Box onClick={onClose} cursor="pointer">
              <ColorIcon
                name="icon-x-mono"
                color={baseColors.label_assistive_1}
                size={24}
              />
            </Box>
          </Box>

          {/* 제목은 의미 단위로 끊어 좁은 화면에서도 한 글자만 떨어지지 않게 한다 */}
          <BaseStack spacing={baseSpacing.sp_12}>
            <Box
              className="headline_1_sb"
              color={baseColors.label_normal}
              textAlign="center"
            >
              상담이 필요한 상황을
              <br />
              선택해주세요
            </Box>

            {variant !== "faq" && (
              <Box
                className="body_1_normal_m"
                color={baseColors.label_normal}
                textAlign="center"
              >
                현재 상황에 맞게
                <br />
                가장 빠른 방법으로 도와드릴게요.
              </Box>
            )}
          </BaseStack>

          <Box height="1px" bg={baseColors.line_accent} />

          <CallModalSection caption="임종하셨거나 임박하신가요? 빠르게 도와드릴게요.">
            <BaseButton
              type="primary"
              size="large"
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "gnb",
                    trigger_and_target: "call_modal_CTA",
                    properties: eventProperties,
                  },
                });
                call("1666-9784");
              }}
            >
              <Box className="body_1_normal_sb">
                임종 접수 / 긴급 상담 연결하기
              </Box>
            </BaseButton>
          </CallModalSection>

          {variant === "member" && (
            <CallModalSection
              caption="이미 고이 프라임에 가입하셨나요?"
              captionClassName="caption_1_sb"
              captionColor={baseColors.accent_blue}
            >
              <BaseButton
                type="secondary"
                size="large"
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "gnb",
                      trigger_and_target: "call_modal_faq",
                      properties: eventProperties,
                    },
                  });
                  onClose();
                  router.push("/faq/");
                }}
              >
                <Box className="body_1_normal_sb">
                  해지·결제·계약서 바로 해결하기
                </Box>
              </BaseButton>
            </CallModalSection>
          )}

          {variant === "faq" ? (
            <CallModalSection caption="가입, 해지, 결제 등 그 외 문의">
              <ConsultCallButton
                label="고객센터 상담 연결하기"
                tel="1666-9784"
                appearance="secondary"
                openNotice={CONSULT_HOURS_LABEL}
                onClick={() =>
                  customEvent({
                    newGtm: {
                      path: "gnb",
                      trigger_and_target: "call_modal_consult",
                      properties: eventProperties,
                    },
                  })
                }
              />
            </CallModalSection>
          ) : (
            <CallModalSection caption="고이 상조가 궁금하신가요?">
              <BaseButton
                type="secondary"
                size="large"
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "gnb",
                      trigger_and_target: "call_modal_CTA2",
                      properties: eventProperties,
                    },
                  });
                  router.push("/call-lead/");
                }}
              >
                <Box className="body_1_normal_sb">고이 상조 상담 신청하기</Box>
              </BaseButton>
            </CallModalSection>
          )}
        </BaseStack>
      }
    />
  );
}

/** 모달 안의 안내 문구 + 버튼 한 묶음 */
function CallModalSection({
  caption,
  captionClassName = "caption_1_m",
  captionColor = baseColors.label_normal,
  children,
}: {
  caption: string;
  captionClassName?: string;
  captionColor?: string;
  children: ReactNode;
}) {
  return (
    <BaseStack spacing={baseSpacing.sp_8} width="100%">
      <Box className={captionClassName} color={captionColor} textAlign="center">
        {caption}
      </Box>
      {children}
    </BaseStack>
  );
}
